import { renderableSuggestionsIn } from '@orn/global/assistant/inlineMarkup.js';
import formatClockTime from '@orn/shared-web/utils/formatClockTime';
import formatSessionDivider from '@orn/shared-web/utils/formatSessionDivider';

import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';

/**
 * Idle gap between two consecutive renderable messages that opens a new
 * "session" — the transcript draws a date-stamped rule above the later one.
 * There is no client-side thread model, so a long quiet stretch is the only
 * signal a fresh sitting has begun.
 */
export const SESSION_GAP_THRESHOLD_MS = 60 * 60 * 1000;

/**
 * Smaller idle gap that earns a bare clock-time separator (no date — the
 * session rule already carries the day). Below this, consecutive messages share
 * the separator above the first.
 */
export const MINUTE_GAP_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * A separator drawn above a message bubble: either the full session rule (a
 * hairline-flanked date stamp opening a new sitting) or the lighter clock-time
 * marker. At most one is emitted per message; a session rule always wins.
 */
export type TranscriptDivider = { kind: 'session'; label: string } | { kind: 'minute'; label: string };

/**
 * A message earns a bubble only if it has visible content or attachments —
 * except an alert card, whose whole payload is its `alertId` and whose text is
 * read from the live callout at render. Judging it on `content` would drop it
 * as an empty row.
 */
export function isRenderableMessage(message: IConversationMessage): boolean {
  if (message.kind === 'alert-card') {
    return true;
  }
  if (message.content.trim().length > 0) {
    return true;
  }
  return (message.attachments?.length ?? 0) > 0;
}

/**
 * One unit of rendered transcript: either a tight cluster of consecutive
 * tool-call lines, or a single normal message row.
 */
export type TranscriptRenderItem =
  | { type: 'tool-cluster'; toolCalls: IConversationMessage[] }
  | { type: 'message'; message: IConversationMessage };

/**
 * Group a transcript slice for rendering. Two steps:
 *
 * 1. **Drop rows that render nothing** (`!isRenderableMessage` and not a
 *    tool-call) — chiefly the empty-text assistant rows a tool-only round
 *    projects on rehydration. Left in, they sit BETWEEN this turn's tool-call
 *    lines and split each into its own cluster, reintroducing the wide
 *    inter-bubble gap between activity lines that visually belong together.
 * 2. **Cluster consecutive tool-call lines** so the view can render them with a
 *    tight internal gap while keeping normal spacing around the cluster.
 *
 * Tool-call lines are always kept (they render their goal text). A genuinely
 * renderable bubble between two of them is kept and correctly separates them —
 * only invisible rows are transparent to clustering. Pure — no React.
 */
export function clusterTranscriptRows(rows: IConversationMessage[]): TranscriptRenderItem[] {
  const visible = rows.filter((m) => m.kind === 'tool-call' || isRenderableMessage(m));
  const out: TranscriptRenderItem[] = [];
  let i = 0;
  while (i < visible.length) {
    if (visible[i].kind === 'tool-call') {
      const start = i;
      while (i < visible.length && visible[i].kind === 'tool-call') {
        i += 1;
      }
      out.push({ type: 'tool-cluster', toolCalls: visible.slice(start, i) });
    } else {
      out.push({ type: 'message', message: visible[i] });
      i += 1;
    }
  }
  return out;
}

/**
 * Decide which messages get a separator above their bubble, and which kind.
 *
 * Both separators are gap-driven, off the elapsed time since the previous
 * renderable message:
 *
 * - **session** — gap ≥ {@link SESSION_GAP_THRESHOLD_MS}: a hairline-flanked
 *   date stamp opening a fresh sitting. The very first renderable message always
 *   gets one too, so the transcript opens with a dated anchor.
 * - **minute** — gap ≥ {@link MINUTE_GAP_THRESHOLD_MS} (but under a session): a
 *   bare clock time (no date — the session rule above already carries the day).
 *
 * A larger gap wins outright (session is checked first), and below the minute
 * threshold a message carries no separator. Tool-call and non-renderable rows
 * are skipped entirely and neither carry nor reset the gap baseline, so the
 * measurement runs unbroken across the thinking-box split the view draws between
 * in-flight and queued turns.
 */
export function buildTranscriptDividers(
  messages: IConversationMessage[],
  now: Date = new Date()
): Map<string, TranscriptDivider> {
  const dividers = new Map<string, TranscriptDivider>();
  let prevTimestampMs: number | null = null;

  for (const message of messages) {
    // Tool-call lines are meta — they neither show a separator nor reset the
    // gap baseline (same treatment as non-renderable rows).
    if (message.kind === 'tool-call' || !isRenderableMessage(message)) {
      continue;
    }
    const timestampMs = message.timestamp.getTime();
    const gapMs = prevTimestampMs === null ? null : timestampMs - prevTimestampMs;
    prevTimestampMs = timestampMs;

    // gapMs === null marks the first renderable message — it always opens with
    // the dated session rule.
    if (gapMs === null || gapMs >= SESSION_GAP_THRESHOLD_MS) {
      dividers.set(message.id, { kind: 'session', label: formatSessionDivider(message.timestamp, now) });
    } else if (gapMs >= MINUTE_GAP_THRESHOLD_MS) {
      dividers.set(message.id, { kind: 'minute', label: formatClockTime(message.timestamp) });
    }
  }

  return dividers;
}

/**
 * Ids of renderable message bubbles that continue a same-sender run and should
 * render tucked tight under the bubble before them — so a burst of messages
 * from one side reads as one group instead of evenly-spaced singles.
 *
 * A bubble is grouped when the previous VISIBLE row (tool-call lines and
 * non-rendering rows excluded the same way the view excludes them) is a message
 * bubble from the same sender AND the bubble carries no divider (a minute or
 * session separator already provides the visual break — a session-boundary
 * bubble must never be tucked tight under the prior sitting). Tool-call lines
 * never group and always break a run; the first bubble of any run is never
 * grouped. Pure — no React.
 */
export function computeGroupedMessageIds(
  messages: IConversationMessage[],
  dividers: ReadonlyMap<string, TranscriptDivider>
): Set<string> {
  const grouped = new Set<string>();
  const visible = messages.filter((m) => m.kind === 'tool-call' || isRenderableMessage(m));
  for (let i = 1; i < visible.length; i += 1) {
    const cur = visible[i];
    const prev = visible[i - 1];
    if (cur.kind === 'tool-call' || prev.kind === 'tool-call') {
      continue;
    }
    if (dividers.has(cur.id)) {
      continue;
    }
    if (cur.role === prev.role) {
      grouped.add(cur.id);
    }
  }
  return grouped;
}

/**
 * Assistant rows whose suggestion buttons have been spent: the user took one of
 * them, so the invitation is answered and every suggestion button on that row
 * comes down — the tapped one and the ones beside it alike.
 *
 * Only the suggestion buttons. The inline entity chips a reply draws for a
 * transaction, merchant, or account are part of what it says, not an
 * invitation, so they stay exactly as they were.
 *
 * Keyed on a text match, because a chip click reaches the client as an
 * ordinary user turn — its text is the only thing tying it back to the row
 * that offered it. Both sides are persisted, so a reloaded transcript hides
 * the chips it hid before.
 *
 * A label is credited to the most recent row offering it, so when a later reply
 * repeats a suggestion the earlier row keeps its chips until its own is taken.
 */
export function computeSpentSuggestionIds(messages: readonly IConversationMessage[]): Set<string> {
  const spent = new Set<string>();
  const offeredBy = new Map<string, string>();

  for (const message of messages) {
    if (message.role === 'user') {
      const offeringRowId = offeredBy.get(message.content.trim());
      if (offeringRowId !== undefined) {
        spent.add(offeringRowId);
      }
      continue;
    }
    for (const label of renderableSuggestionsIn(message.content)) {
      offeredBy.set(label.trim(), message.id);
    }
  }

  return spent;
}
